import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isApiError } from '@nucleus-commerce/api-client/transport';

import { captureError } from '../monitoring';
import './meta';

export interface CreateQueryClientOptions {
  /** Shows a translated error toast. Wired to sonner + useErrorMessage by the app. */
  notifyError: (error: unknown) => void;
  notifySuccess?: ((messageKey: string) => void) | undefined;
}

/**
 * Global query defaults and the mutation error policy:
 *  - queries: 30s stale, retry only retryable ApiErrors (network/timeout/5xx/429), refetch on focus
 *  - mutations: no retry, toast every error except validation (forms own those) unless meta.silentError
 *  - 401 is handled by the transport hook (redirect), so it is never toasted
 */
export function createQueryClient({
  notifyError,
  notifySuccess,
}: CreateQueryClientOptions): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.silentError) return;
        if (isApiError(error) && error.kind === 'unauthorized') return;
        captureError(error, { queryKey: query.queryKey });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.silentError) return;
        if (
          isApiError(error) &&
          (error.kind === 'validation' ||
            error.kind === 'unauthorized' ||
            error.kind === 'cancelled')
        ) {
          return;
        }
        notifyError(error);
        captureError(error, { mutationKey: mutation.options.mutationKey });
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        const meta = mutation.meta;
        if (meta?.successMessage && notifySuccess) notifySuccess(meta.successMessage);
        for (const key of meta?.invalidates ?? []) {
          void client.invalidateQueries({ queryKey: key });
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: (failureCount, error) => failureCount < 2 && isApiError(error) && error.retryable,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        throwOnError: (error) =>
          isApiError(error) && (error.kind === 'forbidden' || error.kind === 'not_found'),
      },
      mutations: {
        retry: 0,
      },
    },
  });
  return client;
}
